import React from 'react';
import styled, { css, DefaultTheme } from 'styled-components';

import { Margin } from 'src/packages/core';
import { hexToRGBA } from 'src/utils/hex-to-rgba';

import { InputProps, InputSize } from './Input.types';

export const getColorStyles = (isInvalid: boolean, readOnly: boolean, theme: DefaultTheme) => {
  if (isInvalid) {
    return css`
      color: ${theme.colors.text.primary};
      border-color: ${theme.colors.palette.red} !important;
      background-color: transparent !important;
    `;
  }

  if (readOnly) {
    return css`
      color: ${theme.colors.text.secondary};
      border-color: transparent !important;
      background-color: ${theme.colors.background.secondary.BG4} !important;
      user-select: none;
    `;
  }

  return css`
    color: ${theme.colors.text.primary};
    border-color: ${theme.colors.border.middle};
    background-color: transparent !important;
  `;
};

export const StyledInputContainer = styled((props) => <Margin {...props} />)<InputProps>`
  position: relative;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.theme.rtl ? 'flex-end' : 'flex-start')};
  gap: 4px;
  width: ${(props) => props.width ?? 'fit-content'};
`;

export const InputWrapper = styled.div<{ defaulttheme: DefaultTheme }>`
  position: relative;
  width: 100%;
`;

export const ElementWrapper = styled.div<{ side: 'left' | 'right'; defaulttheme: DefaultTheme }>`
  position: absolute;
  top: 50%;
  left: ${(props) => props.side === 'left' && '12px'};
  right: ${(props) => props.side === 'right' && '12px'};
  transform: translateY(-50%);
  cursor: pointer;
  display: flex;
  align-items: center;

  svg {
    height: 20px;
    width: 20px;

    &:hover {
      > path[stroke] {
        stroke: ${({ defaulttheme }) => defaulttheme.colors.palette.primary};
      }

      > path[fill] {
        fill: ${({ defaulttheme }) => defaulttheme.colors.palette.primary};
      }
    }
  }

  ${({ theme }) => theme.mediaQueries.md} {
    svg {
      height: 16px;
      width: 16px;
    }
  }
`;

export const StyledInput = styled.input<InputProps & { defaulttheme: DefaultTheme }>`
  border-width: 1px;
  border-style: solid;
  border-color: ${({ defaulttheme }) => defaulttheme.colors.border.darker};
  border-radius: ${(props) => props.borderRadius};
  outline: 0;
  width: 100%;
  padding-left: ${(props) => (props.leftElement ? '42px' : '12px')};
  padding-right: ${(props) => (props.rightElement || props.type === 'password' ? '42px' : '12px')};
  height: ${(props) => (props.variant === 'secondary' ? InputSize.SMALL : InputSize.DEFAULT)};
  ${(props) => props.colorStyles};
  direction: ltr;
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.typographyScale.desktop.p2.fontSize};
  font-weight: ${({ theme }) => theme.typographyScale.desktop.p2.fontWeight};
  line-height: ${({ theme }) => theme.typographyScale.desktop.p2.lineHeight};
  font-family: ${({ theme }) => theme.typographyScale.desktop.p2.fontFamily};

  :hover,
  :focus {
    border-color: ${({ defaulttheme }) => defaulttheme.colors.palette.primary};
  }

  ::placeholder {
    unicode-bidi: bidi-override;
    font-size: ${({ theme }) => theme.typographyScale.desktop.p2.fontSize};
    font-weight: ${({ theme }) => theme.typographyScale.desktop.p2.fontWeight};
    line-height: ${({ theme }) => theme.typographyScale.desktop.p2.lineHeight};
    font-family: ${({ theme }) => theme.typographyScale.desktop.p2.fontFamily};
    color: ${({ defaulttheme }) => defaulttheme.colors.text.tertiary};
  }

  :disabled {
    cursor: not-allowed;
    color: ${({ defaulttheme }) => hexToRGBA(defaulttheme.colors.text.primary, 0.2)} !important;
    border: unset !important;
    background-color: ${({ defaulttheme }) =>
      defaulttheme.colors.background.secondary.BG4} !important;
    s &::placeholder {
      color: ${({ defaulttheme }) => defaulttheme.colors.text.tertiary} !important;
    }
  }

  ${({ theme }) => theme.mediaQueries.md} {
    padding-left: ${(props) => (props.leftElement ? '38px' : '12px')};
    padding-right: ${(props) =>
      props.rightElement || props.type === 'password' ? '38px' : '12px'};

    &,
    ::placeholder {
      font-size: ${({ theme }) => theme.typographyScale.mobile.p2.fontSize};
      font-weight: ${({ theme }) => theme.typographyScale.mobile.p2.fontWeight};
      line-height: ${({ theme }) => theme.typographyScale.mobile.p2.lineHeight};
      font-family: ${({ theme }) => theme.typographyScale.mobile.p2.fontFamily};
    }
  }
`;
